/**
 * Ready-made drum loops, assembled from PadLab's own drum one-shots — the port of
 * `LoopLibrary.swift`.
 *
 * These are built by mixing rendered one-shots onto a sample-accurate grid rather than by
 * going through the pattern renderer. That function is the right tool for exporting a *user's*
 * pattern: it needs a project, pads, and assets that already exist as files. Building the
 * library's own loops through it would mean writing a dozen throwaway WAVs and a throwaway
 * project to storage before a single loop could be rendered. Mixing directly is one pass, has
 * no storage dependency, and — because overhanging tails are wrapped back onto the loop's own
 * head — produces a loop that is seamless by construction instead of by crossfade.
 *
 * Every loop carries an accurate `detectedBPM`, which is what the app's tempo matching reads,
 * and the shared `LOOP_TAG`, which is what tells the rest of the app that these are beds
 * rather than one-shots — see `assetIsLoop` in `model/types.ts`.
 */

import { LOOP_TAG, makeEffectChain, makeEffectNode, type EffectChain } from '../../model/types';
import { applyEffectChain, type RenderedAudio } from '../offline-render';
import {
  makeSynthEnvelope,
  makeSynthFilter,
  makeSynthLayer,
  makeSynthPatch,
  percussionPatch,
  renderPatch,
  type PercussionKind,
  type SynthPatch,
} from '../synth';
import {
  finishToRMS,
  libraryRandom,
  makeLibraryEntry,
  mixWrapped,
  type LibraryEntry,
  type LibraryRenderContext,
} from './entry';
import { librarySeed } from './identity';
import { noteFrequency } from './notes';

/**
 * Sixteenth notes. Everything below is positioned on this grid, and a roll is expressed as a
 * fractional step rather than by making the grid finer.
 */
export const STEPS_PER_BAR = 16;

export type LoopStyle = 'boomBap' | 'lofi' | 'house' | 'trap';

export const LOOP_STYLES: readonly LoopStyle[] = ['boomBap', 'lofi', 'house', 'trap'];

export function loopStyleDisplayName(style: LoopStyle): string {
  switch (style) {
    case 'boomBap':
      return 'Boom Bap';
    case 'lofi':
      return 'Lo-Fi';
    case 'house':
      return 'House';
    case 'trap':
      return 'Trap';
  }
}

function loopStyleToken(style: LoopStyle): string {
  switch (style) {
    case 'boomBap':
      return 'boombap';
    case 'lofi':
      return 'lofi';
    case 'house':
      return 'house';
    case 'trap':
      return 'trap';
  }
}

function loopStyleCaption(style: LoopStyle): string {
  switch (style) {
    case 'boomBap':
      return 'Boom-bap drums: dusty kick and snare on the backbeat, swung ghost notes, eighth-note hats. Saturated and compressed.';
    case 'lofi':
      return 'Lo-fi drums: soft kick, brushed backbeat, quiet hats and a sub underneath, run through a bit crusher and a tape wobble.';
    case 'house':
      return 'House drums: four-on-the-floor kick, clap on two and four, offbeat hats. Punchy and bright.';
    case 'trap':
      return 'Trap drums: half-time snare, rolling sixteenth hats with triplet rolls, and an 808 sub following the kick.';
  }
}

/**
 * Sixteenth-note swing, 0 = straight. Applied only to odd sixteenths, which is what pushes the
 * off-beat late and leaves the pulse where it was.
 */
function loopStyleSwing(style: LoopStyle): number {
  switch (style) {
    case 'boomBap':
      return 0.16;
    case 'lofi':
      return 0.24;
    case 'house':
      return 0;
    case 'trap':
      return 0.05;
  }
}

function loopStyleSymbol(style: LoopStyle): string {
  switch (style) {
    case 'boomBap':
      return 'metronome.fill';
    case 'lofi':
      return 'cup.and.saucer.fill';
    case 'house':
      return 'square.grid.4x3.fill';
    case 'trap':
      return 'waveform.path.ecg';
  }
}

/**
 * The bus every loop of this style is glued with.
 *
 * No reverb and no delay anywhere: a loop's tails are wrapped onto its own head before the bus
 * runs, and a time-based effect applied afterwards would leave a tail hanging over the seam
 * that nothing wraps back.
 */
function loopStyleBus(style: LoopStyle): EffectChain {
  switch (style) {
    case 'boomBap':
      return makeEffectChain([
        // Valve asymmetry rather than symmetric clipping: even harmonics are what makes a
        // sampled-off-vinyl kit sound thick instead of just loud.
        makeEffectNode('distortion', { values: { drive: 0.3, tone: 0.44, character: 3, mix: 0.38 } }),
        makeEffectNode('compressor', { values: { threshold: -15, ratio: 4, attack: 0.006, release: 0.11, makeup: 3 } }),
        makeEffectNode('eq', {
          values: {
            lowGain: 1.5,
            lowFreq: 90,
            midGain: -1.8,
            midFreq: 520,
            midQ: 1.0,
            highGain: 1.0,
            highFreq: 9000,
          },
        }),
      ]);

    case 'lofi':
      return makeEffectChain([
        makeEffectNode('highPass', { values: { frequency: 42, resonance: 0 } }),
        makeEffectNode('lowPass', { values: { frequency: 6200, resonance: 1 } }),
        makeEffectNode('bitCrush', { values: { bits: 12, mix: 0.5 } }),
        makeEffectNode('distortion', { values: { drive: 0.24, tone: 0.38, character: 0, mix: 0.3 } }),
        // Tape wow. One chorus voice at a third of a hertz is a slow pitch drift, not a chorus
        // — the tell that the loop came off a worn cassette.
        makeEffectNode('chorus', { values: { rate: 0.33, depth: 0.14, voices: 1, mix: 0.18 } }),
        makeEffectNode('compressor', { values: { threshold: -16, ratio: 3.5, attack: 0.012, release: 0.2, makeup: 3 } }),
      ]);

    case 'house':
      return makeEffectChain([
        makeEffectNode('compressor', { values: { threshold: -14, ratio: 4, attack: 0.004, release: 0.09, makeup: 3 } }),
        makeEffectNode('eq', {
          values: { lowGain: 1.2, lowFreq: 80, midGain: -1.0, midFreq: 400, midQ: 0.9, highGain: 2.0, highFreq: 8000 },
        }),
        makeEffectNode('limiter', { values: { ceiling: -1.0, release: 0.06, drive: 2 } }),
      ]);

    case 'trap':
      return makeEffectChain([
        makeEffectNode('compressor', { values: { threshold: -16, ratio: 3, attack: 0.003, release: 0.08, makeup: 2.5 } }),
        makeEffectNode('eq', {
          values: { lowGain: 2.0, lowFreq: 60, midGain: -0.8, midFreq: 600, midQ: 0.8, highGain: 1.5, highFreq: 10000 },
        }),
        makeEffectNode('limiter', { values: { ceiling: -1.0, release: 0.05, drive: 1.5 } }),
      ]);
  }
}

// MARK: - Voices

type Voice =
  | { kind: 'drum'; template: PercussionKind }
  /**
   * An 808-style sub. The length is part of the identity because a sub's decay is what makes
   * it a note rather than a thud, and two different lengths are two different sounds.
   */
  | { kind: 'sub'; midi: number; milliseconds: number };

function voiceKey(voice: Voice): string {
  return voice.kind === 'drum' ? `drum:${voice.template}` : `sub:${voice.midi}:${voice.milliseconds}`;
}

/**
 * Relative level inside the kit.
 *
 * The synth renderer peak-normalises everything it renders, so a closed hat and a kick both
 * arrive at −1 dBFS. Mixed at face value that is a hi-hat solo with a kick somewhere behind
 * it. These are the balances a kit is actually mixed at.
 */
function voiceMixGain(voice: Voice): number {
  if (voice.kind === 'sub') return 0.92;
  switch (voice.template) {
    case 'kick':
      return 1.0;
    case 'snare':
      return 0.82;
    case 'clap':
      return 0.68;
    case 'hihatClosed':
      return 0.32;
    case 'hihatOpen':
      return 0.28;
    case 'rim':
      return 0.42;
    case 'tom':
      return 0.58;
    case 'cowbell':
      return 0.36;
    default:
      return 0.55;
  }
}

/**
 * The 808-style sub used by the trap and lo-fi loops.
 *
 * A short pitch drop into a long sine decay, low-passed so almost nothing but the fundamental
 * survives, then driven hard. The drive is not for character: it puts harmonics an octave and
 * a fifth above the note so the line is still *followable* on a phone speaker that produces
 * nothing at all below 400 Hz.
 */
export function subPatch(midi: number, seconds: number, seed: bigint): SynthPatch {
  return makeSynthPatch({
    name: `Sub ${midi}`,
    symbolName: 'waveform.path',
    layers: [
      makeSynthLayer({ waveform: 'sine', level: 1.0, resetsPhase: true, startPhase: 0 }),
      makeSynthLayer({ waveform: 'triangle', level: 0.16 }),
    ],
    amplitude: makeSynthEnvelope({
      attack: 0.003,
      hold: 0.02,
      decay: seconds * 0.88,
      sustain: 0,
      release: 0.05,
      curve: 0.45,
    }),
    filter: makeSynthFilter({
      type: 'lowPass',
      cutoff: 170,
      resonance: 0.9,
      envelopeAmount: 3.6,
      envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.05, sustain: 0, release: 0.02, curve: 0.8 }),
    }),
    pitchEnvelope: { amountSemitones: 12, decay: 0.045, curve: 0.8 },
    baseFrequency: noteFrequency(midi),
    duration: seconds,
    drive: 0.38,
    seed,
  });
}

async function renderVoice(voice: Voice, context: LibraryRenderContext): Promise<Float32Array[]> {
  const options = {
    sampleRate: context.sampleRate,
    ...(context.budget === undefined ? {} : { budget: context.budget }),
  };
  if (voice.kind === 'drum') return (await renderPatch(percussionPatch(voice.template), options)).channels;
  const seed = librarySeed(`loop-sub-${voice.midi}`, voice.milliseconds);
  return (await renderPatch(subPatch(voice.midi, voice.milliseconds / 1000, seed), options)).channels;
}

// MARK: - Patterns

interface Hit {
  /** Position in sixteenth notes from the top of the loop. Fractional for rolls. */
  step: number;
  velocity: number;
  pan: number;
}

function hit(step: number, velocity: number, pan = 0): Hit {
  return { step, velocity, pan };
}

interface LoopTrack {
  voice: Voice;
  hits: Hit[];
}

/**
 * The tracks for one style over `bars` bars.
 *
 * Two things every pattern here obeys, because the loops have to survive being measured:
 *
 * 1. **Something accents every quarter note.** Autocorrelation cannot tell a beat from its
 *    half or its double on evidence alone, and a pattern whose loudest regular event is the
 *    backbeat reads as half tempo to any estimator. Accented hats on the quarters cost nothing
 *    musically and make the pulse unambiguous.
 * 2. **Velocity varies.** A drum machine that hits everything at 1.0 sounds like a drum
 *    machine. The variation is drawn from a seeded generator, so a loop is identical every
 *    time it is rendered.
 */
function loopTracks(style: LoopStyle, bars: number, seedSlug: string): LoopTrack[] {
  const random = libraryRandom(seedSlug, bars);
  const kick: Hit[] = [];
  const snare: Hit[] = [];
  const clap: Hit[] = [];
  const closedHat: Hit[] = [];
  const openHat: Hit[] = [];
  const rim: Hit[] = [];
  const subTracks: LoopTrack[] = [];

  const barCount = Math.max(bars, 1);
  for (let bar = 0; bar < barCount; bar++) {
    const origin = bar * STEPS_PER_BAR;
    const isLastBar = bar === barCount - 1;
    const isOddBar = bar % 2 === 1;

    switch (style) {
      case 'boomBap': {
        kick.push(hit(origin + 0, 1.0));
        kick.push(hit(origin + 10, random.range(0.72, 0.84)));
        if (isOddBar) kick.push(hit(origin + 6, random.range(0.55, 0.65)));

        snare.push(hit(origin + 4, 0.98));
        snare.push(hit(origin + 12, random.range(0.94, 1.0)));
        snare.push(hit(origin + 7, random.range(0.24, 0.32)));
        if (isLastBar) snare.push(hit(origin + 15, random.range(0.26, 0.34)));

        // Eighth-note hats, accented on the quarters.
        for (let eighth = 0; eighth <= 14; eighth += 2) {
          const onQuarter = eighth % 4 === 0;
          closedHat.push(
            hit(
              origin + eighth,
              onQuarter ? random.range(0.52, 0.6) : random.range(0.26, 0.34),
              random.range(-0.12, 0.12),
            ),
          );
        }
        if (isOddBar) openHat.push(hit(origin + 14, 0.5));
        rim.push(hit(origin + 11, random.range(0.24, 0.32), 0.2));
        break;
      }

      case 'lofi': {
        kick.push(hit(origin + 0, random.range(0.9, 0.98)));
        kick.push(hit(origin + 11, random.range(0.6, 0.7)));

        snare.push(hit(origin + 4, random.range(0.74, 0.82)));
        snare.push(hit(origin + 12, random.range(0.74, 0.82)));
        rim.push(hit(origin + 14, random.range(0.22, 0.3), -0.22));

        for (let eighth = 0; eighth <= 14; eighth += 2) {
          const onQuarter = eighth % 4 === 0;
          // One dropped hat per bar. A perfectly even hat line is the single biggest tell that
          // a lo-fi loop was programmed rather than played.
          if (!onQuarter && random.chance(0.18)) continue;
          closedHat.push(
            hit(
              origin + eighth,
              onQuarter ? random.range(0.42, 0.5) : random.range(0.18, 0.26),
              random.range(-0.18, 0.18),
            ),
          );
        }

        subTracks.push({ voice: { kind: 'sub', midi: 36, milliseconds: 700 }, hits: [hit(origin + 0, 0.95)] });
        subTracks.push({ voice: { kind: 'sub', midi: 34, milliseconds: 500 }, hits: [hit(origin + 11, 0.8)] });
        break;
      }

      case 'house': {
        for (let quarter = 0; quarter <= 12; quarter += 4) {
          kick.push(hit(origin + quarter, quarter === 0 ? 1.0 : random.range(0.92, 0.97)));
        }
        clap.push(hit(origin + 4, random.range(0.8, 0.88)));
        clap.push(hit(origin + 12, random.range(0.8, 0.88)));

        // Offbeat eighths: closed on the first pair, open on the second, which is the shuffle
        // that makes a straight four-to-the-floor breathe.
        closedHat.push(hit(origin + 2, random.range(0.46, 0.54), -0.1));
        closedHat.push(hit(origin + 10, random.range(0.46, 0.54), -0.1));
        openHat.push(hit(origin + 6, random.range(0.52, 0.6), 0.12));
        openHat.push(hit(origin + 14, random.range(0.52, 0.6), 0.12));
        if (isOddBar) rim.push(hit(origin + 15, random.range(0.26, 0.34), 0.25));
        break;
      }

      case 'trap': {
        kick.push(hit(origin + 0, 1.0));
        kick.push(hit(origin + (isOddBar ? 3 : 6), random.range(0.68, 0.78)));
        kick.push(hit(origin + (isOddBar ? 10 : 11), random.range(0.58, 0.66)));

        // Half-time: one backbeat in the middle of the bar, not two.
        clap.push(hit(origin + 8, 1.0));

        for (let sixteenth = 0; sixteenth < 16; sixteenth++) {
          const onQuarter = sixteenth % 4 === 0;
          closedHat.push(
            hit(
              origin + sixteenth,
              onQuarter ? random.range(0.52, 0.6) : random.range(0.24, 0.32),
              random.range(-0.14, 0.14),
            ),
          );
        }
        // Triplet roll on the last sixteenth of every second bar.
        if (isOddBar) {
          for (let third = 1; third <= 2; third++) {
            closedHat.push(hit(origin + 15 + third / 3, random.range(0.3, 0.4)));
          }
        }
        if (isLastBar) openHat.push(hit(origin + 14, 0.42));

        subTracks.push({ voice: { kind: 'sub', midi: 36, milliseconds: 900 }, hits: [hit(origin + 0, 1.0)] });
        subTracks.push({
          voice: { kind: 'sub', midi: isOddBar ? 39 : 34, milliseconds: 600 },
          hits: [hit(origin + 8, 0.85)],
        });
        break;
      }
    }
  }

  const tracks: LoopTrack[] = [];
  if (kick.length > 0) tracks.push({ voice: { kind: 'drum', template: 'kick' }, hits: kick });
  if (snare.length > 0) tracks.push({ voice: { kind: 'drum', template: 'snare' }, hits: snare });
  if (clap.length > 0) tracks.push({ voice: { kind: 'drum', template: 'clap' }, hits: clap });
  if (rim.length > 0) tracks.push({ voice: { kind: 'drum', template: 'rim' }, hits: rim });
  if (closedHat.length > 0) tracks.push({ voice: { kind: 'drum', template: 'hihatClosed' }, hits: closedHat });
  if (openHat.length > 0) tracks.push({ voice: { kind: 'drum', template: 'hihatOpen' }, hits: openHat });
  tracks.push(...subTracks);
  return tracks;
}

// MARK: - Rendering

export const LOOP_TARGET_RMS_DB = -14;

/**
 * Renders one loop, exactly `bars` bars long at `bpm`.
 *
 * The buffer is sized from the tempo first and everything is mixed *into* that length,
 * wrapping. That order matters: a loop trimmed to length after the fact loses the tails that
 * make it join up, and a loop that is even a few samples long drifts against the grid within
 * four repeats.
 */
export async function renderLoop(
  style: LoopStyle,
  bpm: number,
  bars: number,
  context: LibraryRenderContext,
): Promise<RenderedAudio> {
  const sampleRate = context.sampleRate;
  const budget = context.budget;
  const tempo = Math.min(Math.max(bpm, 40), 240);
  const barCount = Math.max(bars, 1);
  const secondsPerBeat = 60 / tempo;
  const stepSeconds = secondsPerBeat / (STEPS_PER_BAR / 4);
  const totalFrames = Math.max(64, Math.round(barCount * 4 * secondsPerBeat * sampleRate));

  const mix = [new Float32Array(totalFrames), new Float32Array(totalFrames)];

  // Derived from the style, tempo and length, never from a per-process hash: a loop has to be
  // identical every time it is rendered or a preview stops matching its bounce.
  const seedSlug = `loop-${loopStyleToken(style)}-${Math.round(tempo)}`;
  const cache = new Map<string, Float32Array[]>();

  for (const track of loopTracks(style, barCount, seedSlug)) {
    const key = voiceKey(track.voice);
    let buffer = cache.get(key);
    if (buffer === undefined) {
      buffer = await renderVoice(track.voice, context);
      cache.set(key, buffer);
    }
    const voiceGain = voiceMixGain(track.voice);

    for (const entry of track.hits) {
      // Swing pushes odd sixteenths late. Rolls sit on fractional steps and are left alone —
      // they are already inside a beat that has been swung as a whole.
      const isWholeStep = entry.step === Math.round(entry.step);
      const swing = isWholeStep && Math.trunc(entry.step) % 2 === 1 ? loopStyleSwing(style) * stepSeconds * 0.5 : 0;
      const frame = Math.round((entry.step * stepSeconds + swing) * sampleRate);
      mixWrapped(mix, buffer, frame, voiceGain * Math.min(Math.max(entry.velocity, 0), 1), entry.pan);
    }
    if (budget !== undefined) await budget.tick();
  }

  const processed = await applyEffectChain(mix, loopStyleBus(style), {
    sampleRate,
    tempo,
    ...(budget === undefined ? {} : { budget }),
  });

  finishToRMS(processed, LOOP_TARGET_RMS_DB);
  return { channels: processed, sampleRate };
}

// MARK: - Catalogue

export interface LoopVariant {
  style: LoopStyle;
  bpm: number;
  bars: number;
}

/**
 * The loops the library ships.
 *
 * Two bars everywhere except trap: at 140 BPM two bars is 3.4 seconds, and while that is
 * enough for a tempo estimator to answer, four bars gives it twice the evidence and gives the
 * pattern room for a roll and a variation. Every tempo a beat maker starts from — 80, 90, 100,
 * 120, 140 — is covered by at least one style.
 */
export const LOOP_VARIANTS: readonly LoopVariant[] = [
  { style: 'boomBap', bpm: 80, bars: 2 },
  { style: 'boomBap', bpm: 90, bars: 2 },
  { style: 'boomBap', bpm: 100, bars: 2 },
  { style: 'lofi', bpm: 80, bars: 2 },
  { style: 'lofi', bpm: 90, bars: 2 },
  { style: 'lofi', bpm: 100, bars: 2 },
  { style: 'house', bpm: 120, bars: 2 },
  { style: 'trap', bpm: 140, bars: 4 },
];

export const loopEntries: readonly LibraryEntry[] = LOOP_VARIANTS.map((variant) => {
  const bpmLabel = Math.round(variant.bpm).toString();
  return makeLibraryEntry({
    id: `loop-${loopStyleToken(variant.style)}-${bpmLabel}`,
    fileName: `loop-${loopStyleToken(variant.style)}-${bpmLabel}.wav`,
    name: `${loopStyleDisplayName(variant.style)} ${bpmLabel}`,
    caption: `${loopStyleCaption(variant.style)} ${variant.bars} bars at ${bpmLabel} BPM.`,
    // `LOOP_TAG` rather than the string, because this is the *declaration* the app's loop
    // test reads back: assigning one of these to a pad defaults it to looping playback with
    // bar quantise, its sequencer track fires once per pass, and the slicer offers to chop
    // it. `assetIsLoop` in `model/types.ts` is the one place that decision is made.
    tags: [LOOP_TAG, 'drums', loopStyleDisplayName(variant.style).toLowerCase()],
    family: 'loops',
    group: loopStyleDisplayName(variant.style),
    color: 'cyan',
    symbolName: loopStyleSymbol(variant.style),
    detectedBPM: variant.bpm,
    loops: true,
    render: (context) => renderLoop(variant.style, variant.bpm, variant.bars, context),
  });
});
